<template>
  <v-card class="fixed-email-window elevation-12" v-bind:class="{
    show: emailWindowToggled,
    fullscreen: fullscreenToggled,
    minimise: minimiseToggled,
  }">
    <v-toolbar dark color="secondary">
      <h3 class="pr-4">{{ title }}</h3>
      <v-spacer></v-spacer>
      <v-toolbar-items>

        <v-tooltip left color="secondary" v-if="!fullscreenToggled">
          <v-btn slot="activator" class="ma-0 mt-3 mr-1" icon dark @click="minimiseToggled = !minimiseToggled">
            <v-icon v-html="minimiseToggled ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
              "></v-icon>
          </v-btn>
          <span>Minimise Window</span>
        </v-tooltip>
        <v-tooltip left color="secondary" v-if="!fullscreenToggled">
          <v-btn slot="activator" class="ma-0 mt-3 mr-1 make-fullscreen" icon dark @click="fullscreenToggled = true">
            <v-icon>fullscreen</v-icon>
          </v-btn>
          <span>Toggle Fullscreen</span>
        </v-tooltip>
        <v-tooltip left color="secondary" v-if="fullscreenToggled">
          <v-btn slot="activator" class="ma-0 mt-3 mr-1 close-fullscreen" icon dark @click="fullscreenToggled = false">
            <v-icon>fullscreen_exit</v-icon>
          </v-btn>
          <span>Exit Fullscreen</span>
        </v-tooltip>
        <v-tooltip left color="secondary" v-if="!compose">
          <v-btn slot="activator" class="ma-0 mt-3 mr-1" icon dark @click="emailWindowToggled = !emailWindowToggled">
            <v-icon>close</v-icon>
          </v-btn>
          <span>Close Window</span>
        </v-tooltip>
      </v-toolbar-items>
    </v-toolbar>
    <v-flex px-4 pt-3 pb-2>

      <EmailAddressList ref="toField" :allParticipants="allParticipants" :previousParticipants="currentRecipients"
        :displayCCbuttons="true" label="To" tabindex="1" v-on:selectionChanged="toSelectionChanged" v-on:ccToggled="handleCcToggled"
        v-on:bccToggled="handleBccToggled" v-on:removeRecipient="removeToRecipient">
      </EmailAddressList>

      <EmailAddressList :allParticipants="allParticipants" :previousParticipants="currentRecipientsCc"
        :displayCCbuttons="false" label="Cc" tabindex="4" v-if="ccToggled" v-on:selectionChanged="ccSelectionChanged"
        v-on:removeRecipient="removeCcRecipient">
      </EmailAddressList>

      <EmailAddressList :allParticipants="allParticipants" :previousParticipants="currentRecipientsBcc"
        :displayCCbuttons="false" label="Bcc" tabindex="5" v-if="bccToggled" v-on:selectionChanged="bccSelectionChanged"
        v-on:removeRecipient="removeBccRecipient">
      </EmailAddressList>


      <div class="subject-input-container">
          <v-text-field box label="Subject" tabindex="2" v-model="subject"></v-text-field>
      </div>

      <v-autocomplete :items="allBodyTemplates" label="Response Template" tabindex="6"
        no-data-text="Nothing match your search" item-text="name" item-value="id" v-model="selectedTemplateId" dense
        clearable clear-icon="cancel" v-on:change="templateSelected"
        v-show="allBodyTemplates && allBodyTemplates.length > 0"></v-autocomplete>
      <RichText tabindex="3" :data="message" :field="richField" v-on:update-item="updateMessage"></RichText>
    </v-flex>
    <v-card-actions class="px-4 pt-2 ma-0 pb-3">
      <v-flex grow pt-2>
        <v-btn @click="emitSendMessage()" class="mr-2" color="success" :disabled="sendDisabled">Send</v-btn>
        <AttachmentsDialog :attachments="currentCase.fileAttachments" :allow-select-multiple="true"
          :currentCase="currentCase" :selectedAttachments="selectedAttachments" :user="user" />
        <p v-if="this.selectedAttachments.length === 0"></p>
        <p v-else>{{ this.selectedAttachments.length }} attachments</p>
      </v-flex>
      <v-flex shrink>
        <v-tooltip left color="secondary">
          <v-btn slot="activator" class="ma-0 mt-0" icon @click="deleteComposition()">
            <v-icon color="secondary">delete</v-icon>
          </v-btn>
          <span>Discard Draft</span>
        </v-tooltip>
      </v-flex>
    </v-card-actions>
  </v-card>
</template>

<script>
import EmailAddressList from "@/components/comms/EmailAddressList.vue"
import RichText from "@/components/cases/case-elements/RichText"
import AttachmentsDialog from "@/components/cases/case-elements/AttachmentsDialog.vue"
import { mapState } from 'vuex'

export default {
  name: "EmailComposeCard",
  components: { RichText, AttachmentsDialog, EmailAddressList },
  data: () => ({
    ccToggled: false,
    bccToggled: false,
    fullscreenToggled: false,
    minimiseToggled: false,
    compose: true,
    selectedMultipleChips: [],
    selectedMultipleChipsCc: [],
    selectedMultipleChipsBcc: [],
    subject: "",
    richField: { name: "" },
    message: "",
    selectedTemplateId: undefined,
    searchPrefix: "",
  }),
  mounted() {
    this.selectedTemplateId = this.defaultEmailBodyTemplateId
    if (this.selectedTemplateId) {
      this.templateSelected(this.selectedTemplateId)
    }
    // Initialize subject from conversationSubject when replying
    if (this.replyMode && this.conversationSubject && !this.subject) {
      this.subject = this.formatReplySubject(this.conversationSubject)
    }
    // Focus the To field when form appears
    this.$nextTick(() => {
      if (this.$refs.toField && this.$refs.toField.focusInput) {
        this.$refs.toField.focusInput()
      }
    })
  },
  watch: {
    conversationSubject(newVal) {
      // Update subject when conversationSubject changes (e.g., when opening reply)
      if (this.replyMode && newVal && !this.subject) {
        this.subject = this.formatReplySubject(newVal)
      }
    },
    replyMode(newVal) {
      // Initialize subject when entering reply mode
      if (newVal && this.conversationSubject && !this.subject) {
        this.subject = this.formatReplySubject(this.conversationSubject)
      }
    }
  },
  methods: {
    formatReplySubject(subject) {
      // Add "Re: " prefix if subject doesn't already start with it
      if (!subject) {
        return ""
      }
      const trimmedSubject = subject.trim()
      if (trimmedSubject.toLowerCase().startsWith("re:")) {
        return trimmedSubject
      }
      return `Re: ${trimmedSubject}`
    },
    removeToRecipient: function (recipient) {
      const posRecipient = this.selectedMultipleChips.findIndex(r => r === recipient)
      if (posRecipient >= 0) {
        this.selectedMultipleChips.splice(posRecipient, 1)
      }
    },
    removeCcRecipient: function (recipient) {
      const posRecipient = this.selectedMultipleChipsCc.findIndex(r => r === recipient)
      if (posRecipient >= 0) {
        this.selectedMultipleChipsCc.splice(posRecipient, 1)
      }
    },
    removeBccRecipient: function (recipient) {
      const posRecipient = this.selectedMultipleChipsBcc.findIndex(r => r === recipient)
      if (posRecipient >= 0) {
        this.selectedMultipleChipsBcc.splice(posRecipient, 1)
      }
    },
    toSelectionChanged: function (selection) {
      this.selectedMultipleChips = selection
    },
    handleCcToggled() {
      this.ccToggled = !this.ccToggled
    },
    handleBccToggled() {
      this.bccToggled = !this.bccToggled
    },
    ccSelectionChanged: function (selection) {
      this.selectedMultipleChipsCc = selection
    },
    bccSelectionChanged: function (selection) {
      this.selectedMultipleChipsBcc = selection
    },
    recipientChipLabel: function (recipient) {
      if (recipient) {
        return recipient.displayName || recipient
      }
      return ""
    },
    updateMessage({ data }) {
      this.message = data
    },
    deleteComposition: function (msg) {
      this.message = ""
      this.subject = ""
      this.selectedMultipleChips = []
      this.selectedMultipleChipsCc = []
      this.selectedMultipleChipsBcc = []
      // this.selectedAttachments = [] // clear from state
      this.$store.dispatch("dismissNewMessage")
      /* Need to figure out how to clear the recipients select option */
    },
    emitSendMessage() {
      const recipMap = {
        recipients: this.selectedMultipleChips,
        ccRecipients: this.selectedMultipleChipsCc,
        bccRecipients: this.selectedMultipleChipsBcc
      }
      const newMessage = {
        caseId: this.currentCase.id,
        caseTypeId: this.currentCase.caseTypeId,
        conversationId: this.conversationId,
        subject: this.subject || this.conversationSubject,
        body: this.message,
        recipients: recipMap.recipients,
        ccRecipients: recipMap.ccRecipients,
        bccRecipients: recipMap.bccRecipients,
        replyMode: this.replyMode,
        attachmentIds: this.selectedAttachments.map(att => att.id),
        selectedTemplateId: this.selectedTemplateId
      }
      if (this.conversationId) {
        this.$store.dispatch("replyCaseConversation", { message: newMessage, recipMap })
      } else {
        this.$store.dispatch("sendMessage", { message: newMessage, recipMap })
      }
      this.deleteComposition()
    },
    async templateSelected(selectedTemplateId) {
      this.selectedTemplateId = selectedTemplateId
      const result = await this.$store.dispatch("renderEmailTemplate", { id: selectedTemplateId, payload: this.currentCase.properties })
      this.message = result.rendering
    }
  },
  props: {
    emailWindowToggled: {
      type: Boolean,
      default: false
    },
    caseTitle: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    allParticipants: {
      type: Array,
      required: true
    },
    allBodyTemplates: {
      type: Array,
      required: true
    },
    replyMode: {
      type: Boolean,
      default: false
    },
    currentRecipients: {
      type: Array,
      default: () => []
    },
    currentRecipientsCc: {
      type: Array,
      default: () => []
    },
    currentRecipientsBcc: {
      type: Array,
      default: () => []
    },
    user: {
      type: Object,
      required: true
    },
    currentCase: {
      type: Object,
      required: true
    },
    conversationId: {
      type: Number
    },
    conversationSubject: {
      type: String
    }

  },
  computed: {
    ...mapState({
      selectedAttachments: state => state.folders.selectedAttachments
    }),
    sendDisabled() {
      return !(this.message.length > 0 && (this.subject.length > 0 || this.replyMode) && this.selectedMultipleChips.length > 0)
    },
    defaultEmailBodyTemplate() {
      return this.allBodyTemplates.find(bt => bt.defaultEmailBodyTemplate)
    },
    defaultEmailBodyTemplateId() {
      return this.defaultEmailBodyTemplate ? this.defaultEmailBodyTemplate.id : undefined
    }
  }
}
</script>
